import React, { useCallback, useEffect, useMemo } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from './CartReviewTile'
import useMerchandiseOffer from 'hooks/Merchandise/useMerchandiseOffer'
import { getMerchandiseCategorySearchUrl, getMerchandiseURL } from 'components/Merchandise/merchandiseUtils'
import CartReviewTileImageContent from './CartReviewTileImageContent'
import LoadingCartReviewTileSkeleton from './LoadingCartReviewTileSkeleton'
import { last } from 'lib/array/arrayUtils'
import PriceRowMerchandisePriceStack from 'LuxKit/PricePoints/Merchandise/PriceRowMerchandisePriceStack'
import CondensedCartReviewTile from './CondensedCartReviewTile'
import MerchandiseCartReviewTileContent from './MerchandiseCartReviewTileContent'
import { useUserCartItemState } from 'hooks/UserCart/useUserCartItemState'
import DeletedCartReviewTile from './DeletedCartReviewTile'
import { useAppDispatch } from 'hooks/reduxHooks'
import { userCartItemsDelete, userCartItemsSaveForLater, addToUserCart, userCartItemsHardDelete } from 'actions/UserCartActions'
import MerchandiseCartReviewTileActions from './MerchandiseCartReviewTileActions'
import { generateMerchandiseCartItem } from 'lib/checkout/merchandiseCartItemUtils'
import MerchandiseCartReviewCondensedTileContent from './MerchandiseCartReviewCondensedTileContent'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import TextLink from 'LuxKit/TextLink'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import { generateLoyaltyPointsCalculatorMerchandiseOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import MerchandiseCartReviewTileQuantitySelector from './MerchandiseCartReviewTileQuantitySelector'
import styled from 'styled-components'
import { rem } from 'polished'

const MerchandiseCondensedTileWrapper = styled.div`
  max-width: ${rem(500)};
  width: 100%;
  
  /* Target the Element inside the horizontal Group to make it grow and push delete button right */
  > * > * > * > div:first-child {
    flex-grow: 1;
  }
`

interface Props {
  /**
   * The 'primary' item being used for display on this tile
   */
  primaryItem: App.Checkout.MerchandiseItem
  /**
   * All the items represented by this tile
   *
   * They should all be the same offer id/variant id
   * (this is how we determine 'quantity' of an item)
   */
  items: Array<App.Checkout.MerchandiseItem>
  tileVariant?: 'default' | 'condensed'
}

function MerchandiseCartReviewTile(props: Props) {
  const { primaryItem, items, tileVariant = 'default' } = props

  const dispatch = useAppDispatch()

  const { deleted, saveForLater } = useUserCartItemState(primaryItem.itemId)
  const [merchandise, fetching, error] = useMerchandiseOffer(primaryItem.advertId)
  const [category] = useMerchandiseCategory({ id: merchandise?.category?.id })

  const variant = useMemo(() => {
    return merchandise?.variants.find(variant => variant.id === primaryItem.variantId)
  }, [merchandise, primaryItem.variantId])

  const onDeleteCartItem = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    dispatch(userCartItemsDelete(items.map(item => item.itemId)))
  }, [dispatch, items])

  const onSaveCartItemForLater = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    e.preventDefault()
    dispatch(userCartItemsSaveForLater(items.map(item => item.itemId)))
  }, [dispatch, items])

  const onAddItem = useCallback(() => {
    if (merchandise && variant) {
      const newItem = generateMerchandiseCartItem(merchandise, variant)
      dispatch(addToUserCart([newItem], true))
    }
  }, [dispatch, merchandise, variant])

  const onRemoveItem = useCallback(() => {
    dispatch(userCartItemsHardDelete([last(items).itemId]))
  }, [dispatch, items])

  // No UI yet for 'unavailable' situations, will add later
  // const unavailable = variant && variant?.inventory < items.length
  const soldOut = variant && !variant?.inventory
  const totalPrice = (variant?.price ?? 0) * items.length

  const pointsConfig = useMemo<LoyaltyPointsEarningConfiguration | undefined>(() => {
    if (!soldOut && totalPrice) {
      return {
        calculationRequests: [generateLoyaltyPointsCalculatorMerchandiseOptions(variant, {
          price: totalPrice,
        })],
        calculationType: 'estimate',
      }
    }
  }, [soldOut, totalPrice, variant])

  useEffect(() => {
    if (variant && soldOut) {
      // Set sold out items to 'save for later' - this automatically makes them not active
      // so they are not included in any pricing calculations etc
      dispatch(userCartItemsSaveForLater(items.map(item => item.itemId), true))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, soldOut])

  if (fetching) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (error || !merchandise || !variant) {
    return <MessageBanner kind="critical" density="compact" icon={<LineInfoCircleIcon />}>
      Your item is not available for purchase at the moment. Please try again later.
    </MessageBanner>
  }

  if (deleted) {
    return <DeletedCartReviewTile title={merchandise.name} itemIds={primaryItem.itemId} />
  }

  const image = variant?.images[0]
  const merchandiseUrl = getMerchandiseURL(merchandise)
  const similarItemsUrl = category ? getMerchandiseCategorySearchUrl(category) : undefined

  if (tileVariant === 'condensed') {
    return <MerchandiseCondensedTileWrapper>
      <CondensedCartReviewTile
        to={merchandiseUrl}
        content={<MerchandiseCartReviewCondensedTileContent
          brand={merchandise.brand.name}
          title={merchandise.name}
          variant={variant}
        />}
        banner={soldOut && <MessageBanner
          kind="warning"
          density="compact"
          icon={<LineInfoCircleIcon />}
        >
          Item is out of stock. <TextLink to={similarItemsUrl} target="_blank">View similar items</TextLink>
        </MessageBanner>}
        imageElement={image ? <CartReviewTileImageContent image={image} objectFit="contain" applyImagePadding /> : undefined}
        price={<PriceRowMerchandisePriceStack
          direction="horizontal"
          size="S"
          variant={variant}
          quantity={items.length}
        />}
        onDelete={onDeleteCartItem}
        quantity={<MerchandiseCartReviewTileQuantitySelector
          quantity={items.length}
          inventory={variant.inventory}
          onAddItem={onAddItem}
          onRemoveItem={onRemoveItem}
        />}
      />
    </MerchandiseCondensedTileWrapper>
  }

  return <CartReviewTile
    to={merchandiseUrl}
    isBookable={!!variant.inventory}
    saveForLater={saveForLater}
    imageElement={image ? <CartReviewTileImageContent image={image} objectFit="contain" applyImagePadding /> : undefined}
    content={<MerchandiseCartReviewTileContent
      brand={merchandise.brand.name}
      title={merchandise.name}
      variant={variant}
      loyaltyPointsEarningConfiguration={pointsConfig}
    />}
    banner={soldOut && <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
      Item is out of stock. <TextLink to={similarItemsUrl} target="_blank">View similar items</TextLink>
    </MessageBanner>}
    price={<PriceRowMerchandisePriceStack
      direction="horizontal"
      size="S"
      variant={variant}
      quantity={items.length}
    />}
    actions={<MerchandiseCartReviewTileActions
      variant={variant}
      itemCount={items.length}
      saveForLater={saveForLater}
      onSaveForLater={onSaveCartItemForLater}
      onDelete={onDeleteCartItem}
      onAddItem={onAddItem}
      onRemoveItem={onRemoveItem}
    />}
  />
}

export default MerchandiseCartReviewTile
